import { DATASET_DROPDOWN_OPTIONS } from '../../../../../components/Filters/SingleTagFilterInput/types';
import { TagsFilterLabelFormatters } from '../../../../../components/Filters/TagsFilterInput/TagsFilterInput';
import {
  YEAR_DROPDOWN_THREE_OPTIONS,
  YEAR_DROPDOWN_TWO_OPTIONS,
} from '../../../../../components/Inputs/DateRangeWithDropdown';
import { User } from '../../../../../contexts/AuthContext/authContext.types';
import { ClassifiersTypes } from '../../../../../contexts/ClassifiersContext/ClassifiersContext.types';
import { FilterBackgrounds } from '../../../../../core/config/mainTheme';
import { BackportingEnums } from '../../../../../generated/compat/backportingEnums';
import {
  FilterInputType,
  LibraryFilterItem,
} from '../../../shared/FinancialDataContext/FinancialDataContext.types';

export const FILTER_COLORS = {
  dataParameters: FilterBackgrounds.FirstChild,
  company: FilterBackgrounds.FirstChild,
  ownershipRelations: FilterBackgrounds.SecondChild,
  financialInfo: FilterBackgrounds.ThirdChild,
  financialRatios: FilterBackgrounds.FourthChild,
  esgCompliance: FilterBackgrounds.FifthChild,
};

export const getFilterColor = (filterName: string): FilterBackgrounds => {
  return FILTER_COLORS[filterName as keyof typeof FILTER_COLORS] ?? '';
};

export const getFilterDefinitions = (user: User | undefined): LibraryFilterItem[] => [
  {
    labelKey: 'dataParameters',
    children: [
      {
        labelKey: 'dataset',
        type: FilterInputType.SingleTagFilter,
        defaultValue: {
          value:
            user?.canAccessPublicDb && !user?.canAccessPrivateDb
              ? DATASET_DROPDOWN_OPTIONS.PUBLIC
              : DATASET_DROPDOWN_OPTIONS.PRIVATE,
        },
        //      access all, filter show, disabled false   user?.canAccessPublicDb == true && user?.canAccessPrivateDb == true
        //      access only to private, filter show, disabled false   user?.canAccessPublicDb == false && user?.canAccessPrivateDb == true
        //      access only public, filter hide, disabled true   user?.canAccessPublicDb == true && user?.canAccessPrivateDb == false
        disabled: user?.canAccessPublicDb && !user?.canAccessPrivateDb,
        mandatory: true,
        options: {
          classifier: ClassifiersTypes.Dataset,
          readableStatementLabelKey: 'companies:filters.readableStatements.dataset',
        },
      },
      {
        labelKey: 'dbVersion',
        type: FilterInputType.SingleTagFilter,
        options: {
          classifier: ClassifiersTypes.DbVersions,
          readableStatementLabelKey: 'companies:filters.readableStatements.dbVersion',
        },
      },
      {
        labelKey: 'accountConsolidation',
        type: FilterInputType.ExactTagsFilter,
        withUnknownSwitch: true,
        unknownSwitchEnabled: false,
        options: {
          classifier: ClassifiersTypes.AccountConsolidation,
        },
      },
      {
        labelKey: 'currency',
        type: FilterInputType.CurrencyFilter,
        defaultValue: { value: 'ACTUAL' as BackportingEnums.CcyEnum },
        options: {
          classifier: ClassifiersTypes.StaticCurrency,
        },
      },
    ],
  },
  {
    labelKey: 'company',
    children: [
      {
        labelKey: 'companyActivity',
        type: FilterInputType.CompanyActivityFilter,
        options: {
          labelFormatter: TagsFilterLabelFormatters.Operator,
          classifierSic: ClassifiersTypes.Sic,
          classifierNace: ClassifiersTypes.Nace,
        },
      },
      {
        labelKey: 'contactInformationAvailability',
        type: FilterInputType.ContactInformationFilter,
        options: {
          classifier: ClassifiersTypes.ContactInfoAvailability,
        },
      },
      {
        labelKey: 'companyName',
        type: FilterInputType.WildcardTagsFilter,
      },
      {
        labelKey: 'infoCompanyIdNumber',
        type: FilterInputType.WildcardTagsFilter,
      },
      {
        labelKey: 'infoCountryWithExclusion',
        type: FilterInputType.CountryTagsExclusionFilterInput,
        options: {
          classifier: ClassifiersTypes.RegionAndCountry,
          excludeClassifier: ClassifiersTypes.RegionAndCountryFull,
        },
      },
      {
        labelKey: 'companyKeywordV2',
        type: FilterInputType.WildcardTagsWithExclusionTagsFilter,
        options: {
          labelFormatter: TagsFilterLabelFormatters.Operator,
        },
      },
      {
        labelKey: 'generalInfo',
        children: [
          {
            labelKey: 'infoLegalForm',
            type: FilterInputType.ExactTagsFilter,
            withUnknownSwitch: true,
            options: {
              classifier: ClassifiersTypes.LegalForm,
            },
          },
          {
            labelKey: 'infoCompanyVatNumber',
            type: FilterInputType.WildcardTagsFilter,
          },
          {
            labelKey: 'infoStatus',
            type: FilterInputType.StatusTagsExclusionFilterInput,
            withUnknownSwitch: true,
            options: {
              classifier: ClassifiersTypes.Status,
            },
          },
          {
            labelKey: 'infoStartDate',
            type: FilterInputType.DateRangeFilter,
            withUnknownSwitch: true,
          },
          {
            labelKey: 'infoYearOfStartDate',
            type: FilterInputType.YearRangeWithOpenEndsFilter,
            withUnknownSwitch: true,
          },
          {
            labelKey: 'infoCessationDate',
            type: FilterInputType.DateRangeFilter,
            withUnknownSwitch: true,
          },
          {
            labelKey: 'infoYearOfCessationDate',
            type: FilterInputType.YearRangeWithOpenEndsFilter,
            withUnknownSwitch: true,
          },
        ],
      },
      {
        labelKey: 'employees',
        children: [
          {
            labelKey: 'employeeCount',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
          },
          {
            labelKey: 'employeeFte',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
          },
        ],
      },
      {
        labelKey: 'activitiesGroup',
        children: [
          {
            labelKey: 'activityKeywordsV2',
            type: FilterInputType.WildcardTagsWithExclusionTagsFilter,
            options: {
              labelFormatter: TagsFilterLabelFormatters.Operator,
            },
          },
          {
            labelKey: 'activityIndustryCodeNace',
            type: FilterInputType.TreeWithExclusionFilter,
            options: {
              classifier: ClassifiersTypes.Nace,
            },
          },
          {
            labelKey: 'activityIndustryCodeSic',
            type: FilterInputType.TreeWithExclusionFilter,
            options: {
              classifier: ClassifiersTypes.Sic,
            },
          },
        ],
      },
      {
        labelKey: 'retiredGroup',
        children: [
          {
            labelKey: 'companyKeyword',
            type: FilterInputType.WildcardTagsWithExclusionTagsFilter,
            options: {
              labelFormatter: TagsFilterLabelFormatters.Operator,
              disabled: !user?.isAdminUser,
            },
          },
          {
            labelKey: 'activityKeywords',
            type: FilterInputType.WildcardTagsWithExclusionTagsFilter,
            options: {
              labelFormatter: TagsFilterLabelFormatters.Operator,
              disabled: !user?.isAdminUser,
            },
          },
        ],
      },
    ],
  },
  {
    labelKey: 'ownershipRelations',
    children: [
      {
        labelKey: 'ownershipRelationsName',
        type: FilterInputType.WildcardTagsFilter,
      },
      {
        labelKey: 'ownersHoldings',
        children: [
          {
            labelKey: 'ownershipHoldingType',
            type: FilterInputType.ExactTagsFilter,
            withUnknownSwitch: true,
            options: {
              classifier: ClassifiersTypes.OwnershipType,
            },
          },
          {
            labelKey: 'ownershipHoldingName',
            type: FilterInputType.WildcardTagsFilter,
            withUnknownSwitch: true,
          },
          {
            labelKey: 'ownershipHoldingNoShareholder',
            type: FilterInputType.SwitchShareholderFilter,
          },
          {
            labelKey: 'ownershipHoldingNoShareholding',
            type: FilterInputType.SwitchShareholdingFilter,
          },
        ],
      },
      {
        labelKey: 'otherPersons',
        children: [
          {
            labelKey: 'otherRelatedPartiesRelationType',
            type: FilterInputType.ExactTagsWithExclusionTagsFilter,
            options: {
              classifier: ClassifiersTypes.RelationType,
            },
          },
          {
            labelKey: 'otherRelatedPartiesManagerName',
            type: FilterInputType.WildcardTagsFilter,
          },
        ],
      },
      {
        labelKey: 'branches',
        children: [
          {
            labelKey: 'branchesCode',
            type: FilterInputType.WildcardTagsFilter,
          },
          {
            labelKey: 'branchesName',
            type: FilterInputType.WildcardTagsFilter,
          },
        ],
      },
    ],
  },
  {
    labelKey: 'financialInfo',
    children: [
      {
        labelKey: 'generalFinancialInfo',
        children: [
          {
            labelKey: 'genFinInfoLastAvailYear',
            type: FilterInputType.YearRangeFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'currencies',
            type: FilterInputType.ExactTagsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
            options: {
              classifier: ClassifiersTypes.Currency,
            },
          },
          // {
          //   labelKey: 'genFinInfoAccountType',
          //   type: FilterInputType.ExactTagsFilter,
          //   withUnknownSwitch: true,
          //   options: {
          //     classifier: ClassifiersTypes.AccountingStandardType,
          //   },
          // },
          {
            labelKey: 'genFinInfoAccountStandard',
            type: FilterInputType.ExactTagsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
            options: {
              classifier: ClassifiersTypes.AccountingStandard,
            },
          },
        ],
      },
      {
        labelKey: 'finInfoAccountAvailability',
        type: FilterInputType.YearRangeWithPeriodCriteriaFilter,
        options: {
          criteriaOptions: YEAR_DROPDOWN_THREE_OPTIONS,
          defaultCriteria: [YEAR_DROPDOWN_THREE_OPTIONS[0].value],
        },
      },
      {
        labelKey: 'finInfoPlAvailability',
        type: FilterInputType.ValuesWithYearPeriodAndYearRangeFilter,
        options: {
          criteriaOptions: YEAR_DROPDOWN_TWO_OPTIONS,
          defaultCriteria: [YEAR_DROPDOWN_TWO_OPTIONS[0].value],
          classifier: ClassifiersTypes.ProfitAndLoss,
        },
      },
      {
        labelKey: 'finInfoBalanceSheetAvailability',
        type: FilterInputType.ValuesWithYearPeriodAndYearRangeFilter,
        options: {
          criteriaOptions: YEAR_DROPDOWN_TWO_OPTIONS,
          defaultCriteria: [YEAR_DROPDOWN_TWO_OPTIONS[0].value],
          classifier: ClassifiersTypes.BalanceData,
        },
      },
      {
        labelKey: 'plCashFlows',
        type: FilterInputType.NumberRangeWithYearsFilter,
        withUnknownSwitch: true,
        unknownSwitchEnabled: false,
      },
      {
        labelKey: 'p&l',
        children: [
          {
            labelKey: 'plTurnover',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plCostOfGoods',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plProfitLoss',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plOperatingExpenses',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plEbitda',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plOperatingProfitLossEbit',
            type: FilterInputType.SwitchWithNumberRangeFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plFinIncome',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plFinExpenses',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plBeforeTax',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plTaxOnProfit',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plAfterTax',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'plRndExpense',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
        ],
      },
      {
        labelKey: 'balance',
        children: [
          {
            labelKey: 'balFixedAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'balIntangibleFixedAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'balCurrentAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'balStockInventories',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'balTotalAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
        ],
      },
    ],
  },
  {
    labelKey: 'financialRatios',
    children: [
      {
        labelKey: 'generalRatios',
        children: [
          {
            labelKey: 'genRatioIntangibleTotalAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioIntangibleTurnover',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioTangibleTotalAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioTangibleTurnover',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioDebtorsTurnover',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioStockInvTotalAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioStockInvTurnover',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioLiabilitiesCostOfGoods',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'genRatioLiabilitiesDebtors',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
        ],
      },
      {
        labelKey: 'profitability',
        children: [
          {
            labelKey: 'profitRatioGrossMargin',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'profitRatioOnCapital',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'profitRatioTotalAssets',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'profitRatioNetMargin',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'profitRatioPlBeforeTaxEmployees',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
        ],
      },
      {
        labelKey: 'specificRatios',
        children: [
          {
            labelKey: 'tpRatioGrossMarkUp',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'tpRatioBerry',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'tpRatioEbit',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'tpNetCostPlus',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'tpRndExpenseRatio',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
        ],
      },
      {
        labelKey: 'liquidityRatios',
        children: [
          {
            labelKey: 'tpEbitdaDebtRatio',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'liquidityRatio',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'liquidityRatioSolvency',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            unknownSwitchEnabled: false,
          },
          {
            labelKey: 'liquidityRatioStock',
            type: FilterInputType.NumberRangeWithYearsFilter,
            withUnknownSwitch: true,
            isPercentage: true,
            unknownSwitchEnabled: false,
          },
        ],
      },
    ],
  },
  {
    labelKey: 'esgCompliance',
    children: [
      {
        labelKey: 'businessInvolvement',
        icon: 'casino',
        disabled: true,
      },
      {
        labelKey: 'globalSanctions',
        icon: 'gppBad',
        disabled: true,
      },
      {
        labelKey: 'controversiesAndGlobalNorms',
        icon: 'gavel',
        disabled: true,
      },
      {
        labelKey: 'carbon',
        icon: 'co2',
        disabled: true,
      },
      {
        labelKey: 'sustainableImpact',
        icon: 'compost',
        disabled: true,
      },
    ],
  },
];

export const FILTERS_WITH_HISTORY = [
  'companyName',
  'infoLegalForm',
  'activityIndustryCodeNace',
  'activityIndustryCodeSic',
  'ownershipHoldingName',
  'ownershipHoldingType',
  'ownershipRelationsName',
  'otherRelatedPartiesRelationType',
  'otherRelatedPartiesManagerName',
  'branchesCode',
  'branchesName',
];
